import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrashAlt, FaPlus, FaTimes, FaSearch } from 'react-icons/fa';
import Button from '../../../DynamicComp/Button';
import GenericTable from '../../../DynamicComp/Table';
import Input from '../../../DynamicComp/Input';
import Selector from '../../../DynamicComp/Selector';
import TextBox from '../../../DynamicComp/TextBox';
import ProductGroupImg from './ProductGroupImg';
import axiosClient, { deleteData, getData, postData, putData } from '../../../../global/Globalapi';


const ProductGroup = () => {
  const baseURL = axiosClient.defaults.baseURL;
  const [formData, setFormData] = useState({ 
    groupName: '', 
    parentName: '', 
    attribute_description: '', 
    cgst: '',
    igst: '',
    sgst: '',
    entryType: 'type' 
  });

  const [product_groupdata, setData] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('type');

  // Attribute Assignment States
  const [showAttributeModal, setShowAttributeModal] = useState(false);
  const [allAttributes, setAllAttributes] = useState([]);
  const [attributeAssignments, setAttributeAssignments] = useState({});
  const [selectedAttributes, setSelectedAttributes] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [variableType, setVariableType] = useState('Variant_Specific');
  const [summary, setSummary] = useState({ Common: 0, Variant_Deterministic: 0, Variant_Specific: 0 });
  
  // Add Attribute Modal States
  const [showAddAttributeModal, setShowAddAttributeModal] = useState(false);
  const [newAttributeName, setNewAttributeName] = useState('');
  const [newAttributeDesc, setNewAttributeDesc] = useState('');
  const [isAddingAttribute, setIsAddingAttribute] = useState(false);

  // Add these states
const [isOptionsVisible, setIsOptionsVisible] = useState(false);
const [newOptionText, setNewOptionText] = useState({});
const [attributeOptions, setAttributeOptions] = useState({}); // Store options per attribute

  const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => {
      setAlert({ show: false, message: '', type: 'info' });
    }, 3000);
  };
  
  const hideAlert = () => setAlert({ show: false, message: '', type: 'info' });

  

  // Fetch all available attributes
  const fetchAllAttributes = async () => {
    try {
      const response = await getData('/options/attributes');
      setAllAttributes(response.data);
    } catch (error) {
      console.error('Error fetching attributes:', error);
    }
  };

  // Fetch assignments for current product type
  const fetchAttributeAssignments = async (productTypeId) => {
    if (!productTypeId) return;
    
    try {
      const response = await getData(`/options/assignments/${productTypeId}`);
      const assignments = {};
      
      response.data.forEach(item => {
        assignments[item.attribute_id] = {
          map_id: item.attribute_product_type_map_id,
          variable_type: item.attribute_field_value_name,
          mandatory: item.mandatory === 1
        };
      });
      
      setAttributeAssignments(assignments);
      
      // Fetch summary
      const summaryResponse = await getData(`/options/summary/${productTypeId}`);
      setSummary(summaryResponse.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const fetchProductGroupsForTable = async () => {
    try {
      const response = await getData(`/product_type`);
      setData(response.data);
    } catch (error) { 
      console.error('Error fetching product groups:', error); 
    }
  };

  const fetchCategoriesForSelector = async () => {
    try {
      const response = await getData('/product_type');
      setCategoryList(response.data);
    } catch (error) { 
      console.error('Error fetching categories for selector:', error); 
    }
  };

  useEffect(() => {
    fetchCategoriesForSelector();
    fetchAllAttributes();
  }, []);

  useEffect(() => {
    fetchProductGroupsForTable();
  }, [activeTab]);

  const handleCancel = () => {
    setFormData({ 
      groupName: '', 
      parentName: '', 
      attribute_description: '', 
      cgst: '',
      igst: '',
      sgst: '',
      entryType: 'type' 
    });
    setImages([]);
    setEditId(null);
    setShowForm(false);
    setIsSubmitting(false);
    setAttributeAssignments({});
    setSelectedAttributes(new Set());
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleImagesUpdate = (updatedImages) => setImages(updatedImages);

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Add null checks to prevent undefined errors
  const groupName = formData.groupName || '';
  const parentName = formData.parentName || '';
  const description = formData.attribute_description || '';
  const cgst = formData.cgst || '0';
  const igst = formData.igst || '0';
  const sgst = formData.sgst || '0';
  
  if (!groupName.trim()) {
    showAlert('Group name is required', "info");
    return;
  }
  
  setIsSubmitting(true);

  try {
    const formDataToSend = new FormData();
    formDataToSend.append('groupName', groupName.trim());
    formDataToSend.append('parentName', parentName);
    formDataToSend.append('attribute_description', description.trim());
    formDataToSend.append('cgst', cgst);
    formDataToSend.append('igst', igst);
    formDataToSend.append('sgst', sgst);
    
    const imagesData = images.map((image, index) => ({ 
      id: image.id || null, 
      file_name: image.file_name || (image.file ? image.file.name : `image_${index}`), 
      displayOrder: image.displayOrder || index + 1, 
      attribute_description: image.attribute_description || '', 
      isNew: !image.id, 
      isDeleted: image.isDeleted || false 
    }));
    
    formDataToSend.append('images_data', JSON.stringify(imagesData));
    images.forEach((image) => { 
      if (image.file instanceof File) { 
        formDataToSend.append('image_files', image.file); 
      } 
    });

    const response = editId
      ? await putData(`/product_typeupdate/${editId}`, formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } })
      : await postData('/product_typeadd', formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } });

    showAlert(response.data.message, "success");
    
    let savedProductId = editId;
    if (!editId && response.data.newGroup?.product_type_id) {
      savedProductId = response.data.newGroup.product_type_id;
    }

    fetchProductGroupsForTable();
    fetchCategoriesForSelector();
    
    // Reset form and close modal
    setFormData({ 
      groupName: '', 
      parentName: '', 
      attribute_description: '', 
      cgst: '',
      igst: '',
      sgst: '',
      entryType: 'type' 
    });
    setImages([]);
    setShowForm(false);
    setIsSubmitting(false);
    
    if (!editId && savedProductId) {
      setEditId(savedProductId);
    }

  } catch (error) {
    console.error('Submission error:', error);
    showAlert(error.response?.data?.message || 'A network error occurred.', "error");
    setIsSubmitting(false);
  }
};
const handleAddOption = async (attributeId, optionText) => {
  const trimmed = String(optionText || '').trim();
  if (!trimmed) {
    showAlert("Option text cannot be empty", "info");
    return;
  }

  const assignment = attributeAssignments[attributeId];
  if (!assignment?.map_id) {
    showAlert("Please save attribute assignments first", "error");
    return;
  }

  // Check for duplicates (case-insensitive)
  const currentOptions = attributeOptions[attributeId] || [];
  const isDuplicate = currentOptions.some(opt => 
    opt.value.trim().toLowerCase() === trimmed.toLowerCase()
  );
  
  if (isDuplicate) {
    showAlert("This option already exists for this attribute", "warning");
    setNewOptionText(prev => ({ ...prev, [attributeId]: '' }));
    return;
  }

  // Add temporary option
  const tempOption = { id: `temp-${Date.now()}`, value: trimmed };
  setAttributeOptions(prev => ({
    ...prev,
    [attributeId]: [...(prev[attributeId] || []), tempOption]
  }));

  setNewOptionText(prev => ({ ...prev, [attributeId]: '' }));
  showAlert("Option added (not saved yet)", "success");
};

const handleRemoveOption = async (attributeId, optionId) => {
  if (!window.confirm("Delete this option?")) return;

  // If temp option, just remove from state
  if (String(optionId).startsWith('temp-')) {
    setAttributeOptions(prev => ({
      ...prev,
      [attributeId]: prev[attributeId].filter(opt => opt.id !== optionId)
    }));
    return;
  }

  // Otherwise delete from database
  try {
    await deleteData(`/options/${optionId}`);
    setAttributeOptions(prev => ({
      ...prev,
      [attributeId]: prev[attributeId].filter(opt => opt.id !== optionId)
    }));
    showAlert("Option deleted", "success");
  } catch (error) {
    showAlert("Failed to delete option", "error");
  }
};

const handleSaveAllOptions = async () => {
  const optionsToSave = [];
  
  Object.entries(attributeOptions).forEach(([attrId, options]) => {
    const assignment = attributeAssignments[attrId];
    if (assignment?.map_id) {
      options.forEach(option => {
        if (String(option.id).startsWith('temp-')) {
          optionsToSave.push({
            attribute_product_type_map_id: assignment.map_id,  
            attribute_value: option.value                       
          });
        }
      });
    }
  });

  if (optionsToSave.length === 0) {
    showAlert("No new options to save", "info");
    return;
  }

  try {
    await Promise.all(optionsToSave.map(opt => postData('/options/options', opt)));
    showAlert("Options saved successfully", "success");
    
    setIsOptionsVisible(false);
  } catch (error) {
    console.error("Error saving options:", error);
    showAlert("Failed to save options", "error");
  }
};
const handleEdit = async (group) => {
  handleCancel();

  setFormData({ 
    groupName: group.product_type_name, 
    parentName: group.parent_product_type_id || '', 
    attribute_description: group.product_type_description || '', 
    cgst: group.cgst || '',
    igst: group.igst || '',
    sgst: group.sgst || '',
    entryType: 'type'
  });
  
  setEditId(group.product_type_id);
  
  // Fetch assignments and get the returned data
  try {
    const response = await getData(`/options/assignments/${group.product_type_id}`);
    const freshAssignments = {};
    
    response.data.forEach(item => {
      freshAssignments[item.attribute_id] = {
        map_id: item.attribute_product_type_map_id,
        variable_type: item.attribute_field_value_name,
        mandatory: item.mandatory === 1
      };
    });
    
    setAttributeAssignments(freshAssignments);
    
    // Fetch summary
    const summaryResponse = await getData(`/options/summary/${group.product_type_id}`);
    setSummary(summaryResponse.data);
    
    // 👇 NOW fetch options using the FRESH data
    const optionPromises = [];
    const attributeIdsWithOptions = [];

    Object.entries(freshAssignments).forEach(([attrId, data]) => {
      if (data.map_id) {
        optionPromises.push(getData(`/options/options_by_map/${data.map_id}`));
        attributeIdsWithOptions.push(attrId);
      }
    });

    if (optionPromises.length > 0) {
      const optionsResults = await Promise.all(optionPromises);
      const newOptions = {};
      
      attributeIdsWithOptions.forEach((attrId, index) => {
        newOptions[attrId] = optionsResults[index].data || [];
      });
      
      setAttributeOptions(newOptions);
    } else {
      // Clear options if no assignments
      setAttributeOptions({});
    }
  } catch (error) {
    console.error("Error in handleEdit:", error);
  }
  
  setShowForm(true);
};

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product group?')) return;
    try {
      const response = await deleteData(`/product_types/${id}`);
      showAlert(response.data.message, "success");
      fetchProductGroupsForTable();
      fetchCategoriesForSelector();
    } catch (error) { 
      showAlert(error.response?.data?.message || "Failed to delete.", "error"); 
    }
  };

  // Attribute Assignment Functions
  const handleAttributeSelect = (attrId) => {
    const newSelection = new Set(selectedAttributes);
    if (newSelection.has(attrId)) {
      newSelection.delete(attrId);
    } else {
      newSelection.add(attrId);
    }
    setSelectedAttributes(newSelection);
  };

  const handleSelectAll = () => {
    const filtered = getFilteredAttributes();
    setSelectedAttributes(new Set(filtered.map(a => a.attribute_id)));
  };

  const handleClearSelection = () => {
    setSelectedAttributes(new Set());
  };

  const handleBulkAssign = () => {
    const newAssignments = { ...attributeAssignments };
    selectedAttributes.forEach(attrId => {
      newAssignments[attrId] = {
        map_id: newAssignments[attrId]?.map_id || null,
        variable_type: variableType,
        mandatory: newAssignments[attrId]?.mandatory || false
      };
    });
    setAttributeAssignments(newAssignments);
    setSelectedAttributes(new Set());
    updateSummary(newAssignments);
  };

  const handleBulkUnassign = () => {
    const newAssignments = { ...attributeAssignments };
    selectedAttributes.forEach(attrId => {
      delete newAssignments[attrId];
    });
    setAttributeAssignments(newAssignments);
    setSelectedAttributes(new Set());
    updateSummary(newAssignments);
  };

  const handleToggleMandatory = (attrId) => {
    const newAssignments = { ...attributeAssignments };
    if (newAssignments[attrId]) {
      newAssignments[attrId].mandatory = !newAssignments[attrId].mandatory;
      setAttributeAssignments(newAssignments);
    }
  };

  const updateSummary = (assignments) => {
    const newSummary = { Common: 0, Variant_Deterministic: 0, Variant_Specific: 0 };
    Object.values(assignments).forEach(assignment => {
      if (newSummary.hasOwnProperty(assignment.variable_type)) {
        newSummary[assignment.variable_type]++;
      }
    });
    setSummary(newSummary);
  };

  const handleSaveAssignments = async () => {
    if (!editId) {
      showAlert('Please save the product group first', 'error');
      return;
    }

    const assignments = Object.entries(attributeAssignments).map(([attribute_id, data]) => ({
      attribute_id: parseInt(attribute_id),
      variable_type: data.variable_type,
      mandatory: data.mandatory
    }));

    try {
      const response = await putData(`/options/assignments/${editId}`, { assignments });
      showAlert(response.data.message, 'success');
      await fetchAttributeAssignments(editId);
      setShowAttributeModal(false);
      setIsOptionsVisible(true);
    } catch (error) {
      console.error('Error saving assignments:', error);
      showAlert('Failed to save assignments', 'error');
    }
  };

  const getFilteredAttributes = () => {
    return allAttributes.filter(attr => 
      attr.attribute_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (attr.attribute_description || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getVariableTypeChip = (type) => {
    const styles = {
      'Variant_Specific': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Variant_Deterministic': 'bg-blue-100 text-blue-800 border-blue-200',
      'Common': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return styles[type] || '';
  };

  const columns = [
    { key: "sno", label: "S.No", align: "center", width: "80px" },
    { key: "parentname", label: "Parent Group" },
    { key: "product_type_name", label: "Name", sortable: true },
    { key: "product_type_description", label: "Description" },
    { key: "cgst", label: "CGST (%)", align: "center" },
    { key: "sgst", label: "SGST (%)", align: "center" },
    { key: "igst", label: "IGST (%)", align: "center" },
    { key: "actions", label: "Actions", align: "center", width: "120px" },
  ];

  const customRenderers = {
    actions: (row) => (
      <div className="flex justify-center items-center gap-2">
        <button 
          onClick={() => handleEdit(row)} 
          className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600" 
          title="Edit"
        >
          <FaEdit />
        </button>
        <button 
          onClick={() => handleDelete(row.product_type_id)} 
          className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700" 
          title="Delete"
        >
          <FaTrashAlt />
        </button>
      </div>
    ),
  };

  const filteredAttributes = getFilteredAttributes();

  return (
    <div className="flex flex-col items-center min-h-screen w-full p-4 sm:p-8 bg-gray-50">

      <div className={`${showForm ? "blur" : ""} p-6 rounded-xl shadow-lg w-full overflow-x-auto mb-8 bg-white`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">Product Type </h2>
          <Button
            variant="danger"
            onClick={() => {
              handleCancel();
              setShowForm(true);
            }}
            className="p-2 rounded-md flex items-center space-x-2 text-sm"
          >
            <FaPlus /><span>Add Type</span>
          </Button>
        </div>

        <div className="mt-4">
          <GenericTable
            headerClassName="bg-red-700 text-white"
            columns={columns}
            data={product_groupdata}
            customRenderers={customRenderers}
            rowKey="product_type_id"
          />
        </div>
      </div>
      
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-6xl relative overflow-y-auto max-h-[90vh]">

            <button
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
              onClick={handleCancel}
            >
              <FaTimes size={20} />
            </button>

            <form onSubmit={handleSubmit} noValidate>
              <h2 className="text-2xl font-bold text-left mb-6 text-gray-800">
                {editId ? 'Edit Product Type' : 'Add Product Type'}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Selector
                  label="Parent Group"
                  options={categoryList
                    .filter(g => !editId || g.product_type_id !== editId)
                    .map(g => ({ value: g.product_type_id, label: g.product_type_name }))}
                  value={formData.parentName}
                  onChange={v => setFormData({ ...formData, parentName: v })}
                  placeholder="Select a Parent Category"
                />
                <Input
                  type="text"
                  label="Type Name *"
                  name="groupName"
                  value={formData.groupName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label htmlFor="cgst" className="block text-sm font-medium text-gray-700">CGST (%)</label>
                  <input
                    type="number"
                    id="cgst"
                    name="cgst"
                    value={formData.cgst}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500"
                  />
                </div>
                <div>
                  <label htmlFor="sgst" className="block text-sm font-medium text-gray-700">SGST (%)</label>
                  <input
                    type="number"
                    id="sgst"
                    name="sgst"
                    value={formData.sgst}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500"
                  />
                </div>
                <div>
                  <label htmlFor="igst" className="block text-sm font-medium text-gray-700">IGST (%)</label>
                  <input
                    type="number"
                    id="igst"
                    name="igst"
                    value={formData.igst}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              </div>

              <TextBox
                label="Description"
                name="attribute_description"
                value={formData.attribute_description}
                onChange={handleChange}
                rows={4}
              />

              {/* <div className="my-8">
                <ProductGroupImg
                  onImagesUpdate={handleImagesUpdate}
                  initialImages={images}
                  imageBasePath="product_group_images" 
                  deletePath="/product_group_images"   
                />
              </div> */}

              {editId && (
                <div className="flex justify-end mb-4">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => setShowAttributeModal(true)}
                    className="px-6 py-2 text-sm"
                  >
                    Next
                  </Button>
                </div>
              )}

              <div className="flex justify-end space-x-4 mt-8 border-t pt-6">
                <Button variant="danger" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : editId ? 'Update' : 'Save'}
                </Button>
                <Button type="button" variant="danger" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

{/* FEATURE OPTIONS POPUP */}
{isOptionsVisible && (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
    <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-4xl relative overflow-y-auto max-h-[90vh]">

      {/* Close button */}
      <button
        className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
        onClick={() => setIsOptionsVisible(false)}
      >
        <FaTimes size={20} />
      </button>

      <h3 className="text-lg font-bold text-gray-800 mb-4">Manage Attribute Options</h3>

      {/* Show ALL assigned attributes */}
      {allAttributes
        .filter(attr => attributeAssignments[attr.attribute_id]) // Show all assigned attributes
        .map(attr => {
          const assignment = attributeAssignments[attr.attribute_id];
          const options = attributeOptions[attr.attribute_id] || [];
          
          return (
            <div key={attr.attribute_id} className="p-4 border rounded-lg bg-white shadow-sm mb-4">
              
              {/* Header with attribute name and type badge */}
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-800">{attr.attribute_name}</h4>
                <span className={`text-xs px-3 py-1 rounded-full border ${getVariableTypeChip(assignment.variable_type)}`}>
                  {assignment.variable_type === 'Variant_Specific' ? 'Variant Specific' :
                   assignment.variable_type === 'Variant_Deterministic' ? 'Deterministic' : 'Common'}
                </span>
              </div>

              {/* Options display */}
              <div className="flex flex-wrap gap-2 mb-3 min-h-[2rem]">
                {options.length > 0 ? (
                  options.map(option => (
                    <span key={option.id} className="flex items-center bg-gray-200 text-gray-800 text-sm font-medium px-3 py-1 rounded-full">
                      {option.value}
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(attr.attribute_id, option.id)}
                        className="ml-2 text-gray-500 hover:text-red-600"
                      >
                        <FaTimes size={12} />
                      </button>
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-400 italic">No options added yet</span>
                )}
              </div>

              {/* Add option input */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newOptionText[attr.attribute_id] || ''}
                  onChange={e => setNewOptionText(prev => ({ ...prev, [attr.attribute_id]: e.target.value }))}
                  placeholder="Add option & press Enter"
                  className="border rounded px-3 py-1.5 text-sm flex-grow"
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption(attr.attribute_id, e.target.value);
                    }
                  }}
                />
                <button
                  type="button"
                  className="px-4 py-1.5 bg-blue-500 text-white rounded text-sm font-semibold hover:bg-blue-600"
                  onClick={() => {
                    handleAddOption(attr.attribute_id, newOptionText[attr.attribute_id]);
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          );
        })}

      {/* Show message if no attributes assigned */}
      {Object.keys(attributeAssignments).length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No attributes assigned yet. Please assign attributes first.
        </div>
      )}

      <div className="flex justify-end mt-4 gap-3">
        <Button
          type="button"
          variant="danger"
          onClick={() => setIsOptionsVisible(false)}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSaveAllOptions}
          disabled={!editId}
        >
          Save All Options
        </Button>
      </div>
    </div>
  </div>
)}

      {/* Attribute Assignment Modal */}
      {showAttributeModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
            
            <div className="p-6 border-b flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Assign Attributes to Variable Types</h2>
                <p className="text-gray-500 text-sm mt-1">Select attributes and assign them to variable types using the panel below</p>
              </div>
              <button
                className="text-gray-500 hover:text-gray-800 p-2"
                onClick={() => setShowAttributeModal(false)}
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="flex flex-1 overflow-hidden">
              {/* Left Panel - Attributes List */}
              <div className="flex-1 p-6 overflow-y-auto border-r">
                
                {/* Toolbar */}
                <div className="flex gap-3 mb-4">
                  <div className="flex-1 relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Filter attributes (e.g. color, size)"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                  <button
                    onClick={handleSelectAll}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-medium"
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleClearSelection}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-medium"
                  >
                    Clear
                  </button>
                </div>

                {/* Attributes List */}
                <div className="space-y-2">
                  {filteredAttributes.map(attr => {
                    const assignment = attributeAssignments[attr.attribute_id];
                    const isSelected = selectedAttributes.has(attr.attribute_id);
                    
                    return (
                      <div
                        key={attr.attribute_id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                          isSelected ? 'bg-red-50 border-red-300' : 'bg-white hover:bg-gray-50 border-gray-200'
                        }`}
                        onClick={() => handleAttributeSelect(attr.attribute_id)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="w-5 h-5 cursor-pointer"
                        />
                        
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-800">{attr.attribute_name}</div>
                          <div className="text-xs text-gray-500">{attr.attribute_description || 'No description'}</div>
                        </div>

                        {assignment && (
                          <div className="flex items-center gap-2">
                            {assignment.mandatory && (
                              <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded">Required</span>
                            )}
                            <span className={`text-xs px-3 py-1 rounded-full border ${getVariableTypeChip(assignment.variable_type)}`}>
                              {assignment.variable_type === 'Variant_Specific' ? 'Variant Specific' :
                               assignment.variable_type === 'Variant_Deterministic' ? 'Deterministic' : 'Common'}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {filteredAttributes.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No attributes found matching your search
                    </div>
                  )}
                </div>

                {/* Selection Bar */}
                {selectedAttributes.size > 0 && (
                  <div className="sticky bottom-0 mt-4 p-4 bg-white border-t-2 border-gray-200 rounded-lg shadow-lg">
                    <div className="flex items-center justify-between gap-4">
                      <div className="font-semibold text-gray-700">
                        <span className="text-red-600">{selectedAttributes.size}</span> attribute(s) selected
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="flex gap-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="variableType"
                              value="Variant_Specific"
                              checked={variableType === 'Variant_Specific'}
                              onChange={(e) => setVariableType(e.target.value)}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">Variant Specific</span>
                          </label>
                          
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="variableType"
                              value="Variant_Deterministic"
                              checked={variableType === 'Variant_Deterministic'}
                              onChange={(e) => setVariableType(e.target.value)}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">Deterministic</span>
                          </label>
                          
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="variableType"
                              value="Common"
                              checked={variableType === 'Common'}
                              onChange={(e) => setVariableType(e.target.value)}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">Common</span>
                          </label>
                        </div>
                        
                        <Button
                          type="button"
                          variant="primary"
                          onClick={handleBulkAssign}
                          className="text-sm px-4 py-2"
                        >
                          Assign Type
                        </Button>
                        
                        <button
                          type="button"
                          onClick={handleBulkUnassign}
                          className="text-sm px-4 py-2 border rounded-lg hover:bg-gray-50"
                        >
                          Clear Assignment
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Panel - Summary & Preview */}
              <div className="w-80 p-6 bg-gray-50 overflow-y-auto">
                
                {/* Summary */}
                <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                  <h3 className="font-bold text-gray-800 mb-3">Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Variant Specific</span>
                      <span className="font-semibold text-emerald-600">{summary.Variant_Specific || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Deterministic</span>
                      <span className="font-semibold text-blue-600">{summary.Variant_Deterministic || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Common</span>
                      <span className="font-semibold text-gray-600">{summary.Common || 0}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="text-sm text-gray-600">Unassigned</span>
                      <span className="font-semibold text-gray-400">
                        {allAttributes.length - Object.keys(attributeAssignments).length}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                  <h3 className="font-bold text-gray-800 mb-3">Example Variant</h3>
                  <div className="text-sm">
                    {Object.entries(attributeAssignments)
                      .filter(([_, data]) => data.variable_type === 'Variant_Specific')
                      .slice(0, 5)
                      .map(([attrId, data]) => {
                        const attr = allAttributes.find(a => a.attribute_id === parseInt(attrId));
                        return attr ? (
                          <div key={attrId} className="py-1">
                            <span className="font-medium">{attr.attribute_name}:</span>
                            <span className="text-gray-600 ml-2">Example Value</span>
                          </div>
                        ) : null;
                      })}
                    
                    {Object.values(attributeAssignments).filter(d => d.variable_type === 'Variant_Specific').length === 0 && (
                      <div className="text-gray-500 text-xs">
                        No variant attributes assigned yet. Assign some Variant Specific attributes to see preview.
                      </div>
                    )}
                  </div>
                </div>

                {/* Tip */}
                <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
                  <strong>Tip:</strong> Select multiple attributes and assign them a type in bulk. Use the filter to quickly find attributes.
                </div>

                {/* Assigned Attributes List */}
                <div className="mt-4 bg-white rounded-lg p-4 shadow-sm">
                  <h3 className="font-bold text-gray-800 mb-3">Assigned Attributes</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {Object.entries(attributeAssignments).map(([attrId, data]) => {
                      const attr = allAttributes.find(a => a.attribute_id === parseInt(attrId));
                      if (!attr) return null;
                      
                      return (
                        <div key={attrId} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                          <div className="flex-1 truncate">
                            <span className="font-medium">{attr.attribute_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleMandatory(parseInt(attrId))}
                              className={`px-2 py-1 rounded text-xs ${
                                data.mandatory 
                                  ? 'bg-red-100 text-red-800' 
                                  : 'bg-gray-200 text-gray-600'
                              }`}
                              title="Toggle mandatory"
                            >
                              {data.mandatory ? 'Required' : 'Optional'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    
                    {Object.keys(attributeAssignments).length === 0 && (
                      <div className="text-gray-500 text-xs text-center py-4">
                        No attributes assigned yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <Button
                type="button"
                variant="danger"
                onClick={() => setShowAttributeModal(false)}
                className="px-6 py-2"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleSaveAssignments}
                className="px-6 py-2"
              >
                Save Assignments
              </Button>
              <Button
              type="button"
              variant="primary"
              onClick={() => setIsOptionsVisible(true)}
              className="text-sm px-4 py-2"
            >
              Manage Options
            </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProductGroup;

